import dayjs from "dayjs";
import { toast } from "react-toastify";
import { currentFeatures, endpoints } from "@/configs/Settings";
import {
  TELECONSULTORIA_GUEST_ALREADY_INVITED,
  teleconsultoriaParticipantType,
} from "../constants";
import { GetEncounter } from "@/services/fhir";
import { fetchAppointmentById } from "./fetchAppointmentById";
import { locationParticipantActor } from "../utils/teleconsultoriaAppointment";
import { PostWhereby } from "@/services/whereby/PostWhereby";
import { useFhirData } from "@/utils/useFhirData";
import type { TeleconsultoriaGuest } from "../types";
import {
  existingActorRefs,
  guestActorsToAdd,
  isSamePractitionerReference,
  teleconsultoriaGuestOfAppointment,
} from "../utils/actors";
import {
  readWherebyRoom,
  wherebyRoomAsArray,
  type WherebyRoom,
} from "../utils/virtualService";
import { pickHostEncounter } from "../utils/teleconsultoriaEncounters";
import {
  patchAppointmentVirtualService,
  patchParticipants,
} from "./patchFhirActors";

function patientIdFromAppointment(
  appointment: fhir5.Appointment,
): string | undefined {
  const reference = useFhirData.findParticipant(
    appointment.participant,
    "Patient",
  )?.reference;
  if (!reference) return undefined;
  if (!currentFeatures.defaultApp) return reference.split("/")[1];
  return reference.split("-")?.[1];
}

/** Exportada para o envio do convite (Task), que garante a sala antes de convidar. */
export async function ensureWherebyRoom(
  appointment: fhir5.Appointment,
): Promise<WherebyRoom> {
  const current = readWherebyRoom(appointment.virtualService);
  if (current?.addressUrl) return current;
  if (!appointment.id) throw new Error("Appointment sem id");

  // Consulta atendida com atraso tem `end` no passado: a sala nasceria expirada.
  const end =
    appointment.end && dayjs(appointment.end).isAfter(dayjs())
      ? dayjs(appointment.end)
      : dayjs().add(60, "minute");
  const response = await PostWhereby({
    endDate: end.format("YYYY-MM-DDTHH:mm:ss-03:00"),
    roomNamePrefix: `${endpoints?.AppName}-whereby`,
    roomNamePattern: "human-short",
  });
  if (!response?.roomUrl) {
    throw new Error("Sala Whereby não criada");
  }

  const room: WherebyRoom = {
    addressUrl: response.roomUrl,
    sessionKey: response.meetingId || "",
  };
  await patchAppointmentVirtualService(appointment.id, room);
  return room;
}

async function findEncounterOfAppointment(
  appointment: fhir5.Appointment,
): Promise<fhir5.Encounter | undefined> {
  const patientId = patientIdFromAppointment(appointment);
  if (!patientId || !appointment.id) return undefined;
  // count 20 + pickHostEncounter: nunca espelha no Encounter do teleconsultor (partOf).
  const bundle = await GetEncounter({
    patientId,
    appointmentId: appointment.id,
    lastUpdaded: true,
    count: 20,
  });
  return pickHostEncounter(bundle?.entry);
}

/** Sem Encounter ainda: o verifyEncounter copia os actors marcados do Appointment. */
async function mirrorOnEncounter(
  appointment: fhir5.Appointment,
  actors: ReturnType<typeof guestActorsToAdd>,
  encounterId?: string,
) {
  if (!actors.length) return;

  const found = await findEncounterOfAppointment(appointment);
  const id = encounterId || found?.id;
  if (!id) return;

  // Só dá para deduplicar quando o Encounter lido é o mesmo do PATCH.
  const participant = found?.id === id ? found.participant : undefined;
  const already = existingActorRefs(participant);
  const missing = actors.filter((actor) => !already.has(actor.reference));
  if (!missing.length) return;

  await patchParticipants("Encounter", id, missing);
}

/** Exportada para o envio do convite: lê o Appointment atualizado (fallback: o recebido). */
export async function loadAppointment(
  appointment: fhir5.Appointment,
): Promise<fhir5.Appointment> {
  if (!appointment.id) return appointment;
  try {
    // Busca por `_id` (padrão do sistema); a leitura `/Appointment/<id>` dava 500.
    // Filtro pela Location do próprio Appointment (exigido pelo scheduling).
    const fresh = await fetchAppointmentById(
      appointment.id,
      locationParticipantActor(appointment)?.reference,
    );
    if (fresh) return fresh;
  } catch (error) {
    console.error("applyTeleconsultoriaInvite.loadAppointment", error);
  }
  return appointment;
}

/**
 * Garante a sala e acrescenta o convidado depois dos actors atuais.
 * Devolve o Appointment em memória para o verifyEncounter não perder o convite.
 */
export async function applyTeleconsultoriaInvite(params: {
  appointment: fhir5.Appointment;
  guest: TeleconsultoriaGuest;
  encounterId?: string;
}): Promise<fhir5.Appointment> {
  const appointment = await loadAppointment(params.appointment);
  const { guest } = params;
  if (!appointment.id) throw new Error("Appointment sem id");

  // Só 1 teleconsultor por evolução. Lido do Appointment recarregado, então
  // vale mesmo com a tela desatualizada. O mesmo profissional segue (idempotente).
  const currentGuest =
    teleconsultoriaGuestOfAppointment(appointment).practitioner;
  if (
    currentGuest &&
    !isSamePractitionerReference(currentGuest.reference, guest.practitioner.reference)
  ) {
    throw new Error(TELECONSULTORIA_GUEST_ALREADY_INVITED);
  }

  const room = await ensureWherebyRoom(appointment);
  const actors = guestActorsToAdd(
    guest,
    existingActorRefs(appointment.participant),
  );

  if (actors.length) {
    await patchParticipants("Appointment", appointment.id, actors);
    try {
      await mirrorOnEncounter(appointment, actors, params.encounterId);
    } catch (error) {
      // O Appointment já foi gravado. Não desfazer o convite.
      console.error("applyTeleconsultoriaInvite.mirrorOnEncounter", error);
      toast.warning(
        "Convite salvo no agendamento, mas o atendimento não foi atualizado.",
      );
    }
  }

  return {
    ...appointment,
    participant: [
      ...(appointment.participant || []),
      ...actors.map((actor) => ({
        type: teleconsultoriaParticipantType(),
        actor: {
          reference: actor.reference,
          display: actor.display,
        },
      })),
    ],
    virtualService: wherebyRoomAsArray(room),
  };
}
